/**
 * Social Proof — 4 variants (marquee, static_grid, press_quote, photo_stories)
 */
import type { SocialProofContent } from '../schemas/social_proof';
import {
  Avatar,
  Body,
  Eyebrow,
  LogoMark,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';

interface SPProps {
  content: SocialProofContent;
}

export function SocialProofMarquee({ content }: SPProps) {
  const logos = content.logos ?? [];
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center' }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        <div className="ls-logo-row">
          {logos.map((logo, i) => (
            <LogoMark key={i} name={logo.name} />
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function SocialProofStaticGrid({ content }: SPProps) {
  const logos = content.logos ?? [];
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, alignItems: 'center' }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        <div
          className="ls-grid ls-grid-4"
          style={{ width: '100%', maxWidth: 900 }}
        >
          {logos.map((logo, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                background: 'var(--ls-bg)',
                border: '1px solid var(--ls-border)',
                borderRadius: 'var(--ls-radius-md)',
              }}
            >
              <LogoMark name={logo.name} />
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function SocialProofPressQuote({ content }: SPProps) {
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, alignItems: 'center', textAlign: 'center' }}>
        {content.quote && (
          <blockquote
            style={{
              fontFamily: 'var(--ls-font-heading)',
              fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.5rem)',
              fontStyle: 'italic',
              maxWidth: 900,
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {content.quote}
          </blockquote>
        )}
        {content.quoteSource && (
          <Body style={{ fontWeight: 500 }}>— {content.quoteSource}</Body>
        )}
        {content.logos && content.logos.length > 0 && (
          <div className="ls-logo-row" style={{ marginTop: 16 }}>
            {content.logos.map((logo, i) => (
              <LogoMark key={i} name={logo.name} />
            ))}
          </div>
        )}
      </div>
    </SectionFrame>
  );
}

export function SocialProofPhotoStories({ content }: SPProps) {
  const stories = content.logos ?? [];
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        <div className="ls-grid ls-grid-3">
          {stories.slice(0, 3).map((s, i) => (
            <div
              key={i}
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <VisualPlaceholder
                src={s.image}
                aspectRatio="4 / 5"
                label={s.name}
              />
              <Avatar name={s.name} size={32} />
              <LogoMark name={s.name} />
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export const socialProofVariants = {
  marquee: SocialProofMarquee,
  static_grid: SocialProofStaticGrid,
  press_quote: SocialProofPressQuote,
  photo_stories: SocialProofPhotoStories,
};
